import type { AuditActivityItem } from '@/services';
import { Button, Dialog, Tag } from '@noorden-insurtech/alba-design-system';
import { ScrollText } from 'lucide-react';
import {
  formatActor,
  formatActorSource,
  formatOccurredAt,
  formatSubjectType,
  formatMetadataKey,
  formatMetadataValue,
} from '../utils';

interface AuditDetailModalProps {
  /** `null` fecha o modal — a tela guarda o registro aberto, não um booleano. */
  activity: AuditActivityItem | null;
  onClose: () => void;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className='min-w-0'>
      <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
        {label}
      </p>
      <p className='text-sm text-alba-gray-800 [overflow-wrap:anywhere]'>
        {value}
      </p>
    </div>
  );
}

/**
 * Registro completo da trilha: o que mudou, os números da operação e o
 * `requestId` que cola o registro ao log técnico no CloudWatch.
 */
export function AuditDetailModal({ activity, onClose }: AuditDetailModalProps) {
  const isOpen = Boolean(activity);

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) onClose();
  };

  const metadataEntries = Object.entries(activity?.metadata ?? {});
  const hiddenChanges = activity
    ? activity.changesTotal - activity.changes.length
    : 0;

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='flex max-h-[92vh] w-[720px] max-w-[95vw] flex-col rounded-3xl p-0'>
          <Dialog.Header className='px-6 pb-2 pt-6'>
            <Dialog.Title className='w-full'>
              <div className='flex items-start gap-3 text-alba-black-500'>
                <ScrollText
                  className='mt-1 h-5 w-5 shrink-0'
                  strokeWidth={1.8}
                />
                <span className='text-lg font-bold leading-6 [overflow-wrap:anywhere]'>
                  {activity?.actionLabel ?? 'Registro de auditoria'}
                </span>
              </div>
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Body className='max-h-[70vh] overflow-y-auto px-6 pb-4 pt-2'>
            {activity && (
              <div className='flex flex-col gap-5'>
                <section className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  <Field
                    label='Data/hora'
                    value={formatOccurredAt(activity.occurredAt)}
                  />
                  <Field label='Autor' value={formatActor(activity)} />
                  <Field
                    label='E-mail'
                    value={activity.actorEmail || 'Não informado'}
                  />
                  <Field
                    label='Origem'
                    value={formatActorSource(activity.actorSource)}
                  />
                  <Field
                    label='Entidade'
                    value={`${formatSubjectType(activity.subjectType)} · ${
                      activity.subjectLabel || activity.subjectId
                    }`}
                  />
                  {/* O id é o que permite reconsultar a trilha desta entidade. */}
                  <Field label='Id da entidade' value={activity.subjectId} />
                </section>

                {activity.description && (
                  <section>
                    <h3 className='mb-2 text-sm font-bold text-alba-black-500'>
                      Descrição
                    </h3>
                    <p className='text-sm text-alba-gray-800 [overflow-wrap:anywhere]'>
                      {activity.description}
                    </p>
                  </section>
                )}

                <section>
                  <div className='mb-2 flex flex-wrap items-center gap-2'>
                    <h3 className='text-sm font-bold text-alba-black-500'>
                      O que mudou
                    </h3>
                    <Tag.Root variant='outline' colorScheme='gray' size='sm'>
                      <Tag.Label>
                        {activity.changesTotal}{' '}
                        {activity.changesTotal === 1
                          ? 'alteração'
                          : 'alterações'}
                      </Tag.Label>
                    </Tag.Root>
                  </div>

                  {activity.changes.length === 0 ? (
                    <p className='text-sm text-alba-gray-600'>
                      Esta ação não registrou alterações campo a campo.
                    </p>
                  ) : (
                    <ul className='divide-y divide-alba-gray-200 overflow-hidden rounded-xl border border-alba-gray-200 bg-white'>
                      {activity.changes.map((change, index) => (
                        <li
                          key={`${change.label}-${index}`}
                          className='flex flex-col gap-1 p-3'
                        >
                          <p className='text-xs font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                            {change.label}
                          </p>
                          <div className='flex flex-wrap items-center gap-2 text-sm'>
                            {change.before && (
                              <>
                                <span className='text-alba-gray-600 line-through [overflow-wrap:anywhere]'>
                                  {change.before}
                                </span>
                                <span className='text-alba-gray-500'>→</span>
                              </>
                            )}
                            <span className='font-medium text-alba-black-500 [overflow-wrap:anywhere]'>
                              {change.after || '—'}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {activity.changesTruncated && hiddenChanges > 0 && (
                    /* O item do DynamoDB guarda no máximo 50 mudanças, mas o
                       total real vai gravado: a tela não pode dar a entender
                       que a operação foi menor do que foi. */
                    <p className='mt-2 rounded-lg border border-alba-warning-orange-400 bg-alba-warning-orange-400/10 px-3 py-2 text-sm text-alba-gray-700'>
                      Outras {hiddenChanges} alterações desta operação não foram
                      gravadas individualmente. Use a exportação em CSV para o
                      resumo completo.
                    </p>
                  )}
                </section>

                {metadataEntries.length > 0 && (
                  <section>
                    <h3 className='mb-2 text-sm font-bold text-alba-black-500'>
                      Números da operação
                    </h3>
                    <dl className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                      {metadataEntries.map(([key, value]) => (
                        <div key={key} className='min-w-0'>
                          <dt className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                            {formatMetadataKey(key)}
                          </dt>
                          <dd className='text-sm text-alba-gray-800 [overflow-wrap:anywhere]'>
                            {formatMetadataValue(value)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                )}

                <section className='grid grid-cols-1 gap-4 border-t border-alba-gray-200 pt-4 sm:grid-cols-2'>
                  {/* Rastreabilidade técnica: `requestId` casa com o log no
                      CloudWatch e `actorSub` é o que alimenta o filtro por autor. */}
                  <Field
                    label='Request id'
                    value={activity.requestId || 'Não registrado'}
                  />
                  <Field label='ID do usuário' value={activity.actorSub} />
                </section>
              </div>
            )}
          </Dialog.Body>

          {/* `pt-0` colava o botao no ultimo campo quando o corpo rola ate
              o fim — o rodape nao tem borda para separar os dois. */}
          <Dialog.Footer className='flex justify-end px-6 pb-6 pt-4'>
            <Button variant='primary' size='md' onClick={onClose}>
              Fechar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
